<template>
  <van-popup
    :value="value"
    position="bottom"
    round
    :close-on-click-overlay="true"
    class="date-picker-popup"
    @input="$emit('input', $event)"
  >
    <div class="dp">
      <!-- Handle bar -->
      <div class="dp__bar">
        <span class="dp__bar-line"></span>
      </div>

      <!-- Title -->
      <h3 class="dp__title">{{ title || 'Select Date' }}</h3>
      <p class="dp__subtitle">{{ subtitle || 'Select a period to filter your history' }}</p>

      <!-- Picker -->
      <van-picker
        ref="picker"
        :columns="columns"
        :default-index="defaultIndexes"
        :visible-item-count="3"
        :item-height="44"
        @change="onPickerChange"
      />

      <!-- Confirm Button -->
      <div class="dp__actions">
        <button class="dp__confirm" @click="onConfirm">
          {{ confirmText || 'Confirm Selection' }}
        </button>
        <button class="dp__cancel" @click="onCancel">
          {{ cancelText || 'Cancel' }}
        </button>
      </div>
    </div>
  </van-popup>
</template>

<script>
export default {
  name: 'DatePickerPopup',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: ''
    },
    cancelText: {
      type: String,
      default: ''
    },
    minDate: {
      type: Date,
      default: () => new Date(2020, 0, 1)
    },
    maxDate: {
      type: Date,
      default: () => new Date()
    },
    defaultDate: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      currentYear: 0,
      currentMonth: 0,
      currentDay: 0
    }
  },
  computed: {
    years() {
      const arr = []
      for (let y = this.minDate.getFullYear(); y <= this.maxDate.getFullYear(); y++) {
        arr.push(y)
      }
      return arr
    },
    months() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      let start = 0
      let end = 11
      if (this.currentYear === this.minDate.getFullYear()) {
        start = this.minDate.getMonth()
      }
      if (this.currentYear === this.maxDate.getFullYear()) {
        end = this.maxDate.getMonth()
      }
      const arr = []
      for (let m = start; m <= end; m++) {
        arr.push({ value: m, text: monthNames[m] })
      }
      return arr
    },
    days() {
      const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
      let start = 1
      let end = daysInMonth
      if (this.currentYear === this.minDate.getFullYear() && this.currentMonth === this.minDate.getMonth()) {
        start = this.minDate.getDate()
      }
      if (this.currentYear === this.maxDate.getFullYear() && this.currentMonth === this.maxDate.getMonth()) {
        end = Math.min(end, this.maxDate.getDate())
      }
      const arr = []
      for (let d = start; d <= end; d++) {
        arr.push(d)
      }
      return arr
    },
    columns() {
      return [
        { values: this.years.map(String), defaultIndex: this.years.indexOf(this.currentYear) },
        { values: this.months.map(m => m.text), defaultIndex: this.months.findIndex(m => m.value === this.currentMonth) },
        { values: this.days.map(String), defaultIndex: this.days.indexOf(this.currentDay) }
      ]
    },
    defaultIndexes() {
      return [
        this.years.indexOf(this.currentYear),
        this.months.findIndex(m => m.value === this.currentMonth),
        this.days.indexOf(this.currentDay)
      ]
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.initDate()
      }
    }
  },
  created() {
    this.initDate()
  },
  methods: {
    initDate() {
      const d = this.defaultDate || this.maxDate || new Date()
      this.currentYear = d.getFullYear()
      this.currentMonth = d.getMonth()
      this.currentDay = d.getDate()
    },
    onPickerChange(picker, values) {
      const yearVal = parseInt(values[0])
      const monthText = values[1]
      const dayVal = parseInt(values[2])

      this.currentYear = yearVal

      // Find the month value from text
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const monthIdx = monthNames.indexOf(monthText)
      if (monthIdx !== -1) {
        this.currentMonth = monthIdx
      }

      // Update month column based on year
      const monthCol = this.months.map(m => m.text)
      picker.setColumnValues(1, monthCol)

      // Clamp month if out of range
      const currentMonthInList = this.months.find(m => m.value === this.currentMonth)
      if (!currentMonthInList && this.months.length > 0) {
        this.currentMonth = this.months[this.months.length - 1].value
        picker.setColumnIndex(1, this.months.length - 1)
      }

      // Update day column based on year + month
      const dayCol = this.days.map(String)
      picker.setColumnValues(2, dayCol)

      // Clamp day if out of range
      if (dayVal && this.days.includes(dayVal)) {
        this.currentDay = dayVal
      } else {
        this.currentDay = this.days[this.days.length - 1]
        picker.setColumnIndex(2, this.days.length - 1)
      }
    },
    onConfirm() {
      const date = new Date(this.currentYear, this.currentMonth, this.currentDay)
      this.$emit('confirm', date)
      this.$emit('input', false)
    },
    onCancel() {
      this.$emit('cancel')
      this.$emit('input', false)
    }
  }
}
</script>

<style lang="less" scoped>
@primary: #A6FF00;
@bg-dark: #0A0E1A;
@surface: #161B2C;

.dp {
  background: @bg-dark;
  padding: 16px 20px 24px;
  border-radius: 16px 16px 0 0;

  &__bar {
    display: flex;
    justify-content: center;
    padding-bottom: 16px;
  }

  &__bar-line {
    width: 40px;
    height: 4px;
    background: #334155;
    border-radius: 2px;
  }

  &__title {
    color: #ffffff;
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    margin-bottom: 4px;
  }

  &__subtitle {
    color: #64748b;
    font-size: 12px;
    text-align: center;
    margin-bottom: 20px;
  }

  &__actions {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }

  &__confirm {
    width: 100%;
    background: @primary;
    color: @bg-dark;
    font-size: 16px;
    font-weight: 900;
    padding: 14px;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.02em;

    &:active {
      opacity: 0.9;
      transform: scale(0.98);
    }
  }

  &__cancel {
    background: transparent;
    border: none;
    color: #94a3b8;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 8px 16px;

    &:active {
      color: #ffffff;
    }
  }
}
</style>

<style lang="less">
// Unscoped - override Vant picker styles for dark theme
.date-picker-popup {
  background: #0A0E1A !important;
  overflow: hidden;

  .van-picker {
    background: transparent !important;
  }

  .van-picker-column__wrapper {
    .van-picker-column__item {
      color: #475569 !important;
      font-size: 16px;
      font-weight: 500;

      &--selected {
        color: #A6FF00 !important;
        font-size: 20px;
        font-weight: bold;
      }
    }
  }

  // The highlight frame around selected item
  .van-picker__frame {
    border: 2px solid rgba(166, 255, 0, 0.3) !important;
    border-radius: 12px;
    background: rgba(166, 255, 0, 0.05);

    &::after {
      display: none;
    }
  }

  // Top and bottom masks (gradient overlays)
  .van-picker__mask {
    background-image: none !important;
    background: transparent !important;
  }

  // Remove default hairlines
  .van-hairline--top-bottom::after {
    display: none;
  }

  .van-picker__toolbar {
    display: none;
  }

  .van-picker__columns {
    background: transparent;
  }
}
</style>
